import { useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfISOWeek,
  endOfISOWeek,
  addDays,
  isSameMonth,
  isToday,
  getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Badge } from '../../../components/Shared/Badge';
import type { FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto } from '../../../api/generated/models';

interface MonthViewProps {
  month: Date;
  entries: FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto[];
  selectedEmployeeIds: string[];
  onDayClick?: (date: Date) => void;
}

const WEEKDAY_LABELS = ['KW', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface DayTaskSummary {
  taskName: string;
  durationHours: number;
}

interface DaySummary {
  date: Date;
  dateStr: string;
  entryCount: number;
  taskCount: number;
  absenceCount: number;
  totalPlannedHours: number;
  employeeNames: string[];
  absenceNames: string[];
  tasks: DayTaskSummary[];
}

function formatHours(hours: number): string {
  const v = Math.round((hours + Number.EPSILON) * 100) / 100;
  return `${v}h`;
}

export function MonthView({ month, entries, selectedEmployeeIds, onDayClick }: MonthViewProps) {
  const calendarWeeks = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const calStart = startOfISOWeek(monthStart);
    const calEnd = endOfISOWeek(monthEnd);

    const weeks: DaySummary[][] = [];
    let current = calStart;

    while (current <= calEnd) {
      const week: DaySummary[] = [];
      for (let i = 0; i < 7; i++) {
        const day = addDays(current, i);
        const dateStr = format(day, 'yyyy-MM-dd');

        const dayEntries = entries.filter(
          (e) =>
            e.date === dateStr &&
            e.employeeId &&
            selectedEmployeeIds.includes(e.employeeId),
        );

        const absenceEntries = dayEntries.filter(
          (e) => e.entryType?.toLowerCase() === 'absence',
        );
        const workEntries = dayEntries.filter(
          (e) => e.entryType?.toLowerCase() !== 'absence',
        );

        const tasks: DayTaskSummary[] = [];
        workEntries.forEach((e) => {
          (e.tasks || []).forEach((t) => {
            tasks.push({
              taskName: t.taskName || 'Aufgabe',
              durationHours: (t.durationMinutes || 0) / 60,
            });
          });
        });

        week.push({
          date: day,
          dateStr,
          entryCount: dayEntries.length,
          taskCount: tasks.length,
          absenceCount: absenceEntries.length,
          totalPlannedHours: dayEntries.reduce((sum, e) => sum + (e.plannedHours || 0), 0),
          employeeNames: dayEntries
            .map((e) => e.employeeName || '')
            .filter(Boolean),
          absenceNames: absenceEntries
            .map((e) => `${e.employeeName}: ${e.absenceTypeName || 'Abwesend'}`)
            .filter(Boolean),
          tasks,
        });
      }
      weeks.push(week);
      current = addDays(current, 7);
    }

    return weeks;
  }, [month, entries, selectedEmployeeIds]);

  return (
    <div className="space-y-3">
      {/* Month Header */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {format(month, 'MMMM yyyy', { locale: de })}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {entries.filter((e) => e.employeeId && selectedEmployeeIds.includes(e.employeeId)).length} Einträge für {selectedEmployeeIds.length} Mitarbeiter
        </p>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)] bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="px-2 py-2.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase border-r border-gray-200 dark:border-gray-700 last:border-r-0"
            >
              {label}
            </div>
          ))}
        </div>

        {/* Calendar Weeks */}
        {calendarWeeks.map((week, weekIdx) => (
          <div
            key={weekIdx}
            className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-gray-200 dark:border-gray-700 last:border-b-0"
          >
            {/* Week Number */}
            <div className="px-2 py-2 text-center text-xs font-medium text-gray-400 dark:text-gray-500 border-r border-gray-200 dark:border-gray-700 flex items-start justify-center pt-3">
              {getISOWeek(week[0].date)}
            </div>

            {/* Days */}
            {week.map((day) => {
              const inMonth = isSameMonth(day.date, month);
              const today = isToday(day.date);
              const hasData = day.entryCount > 0;
              const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

              return (
                <div
                  key={day.dateStr}
                  onClick={() => onDayClick?.(day.date)}
                  className={`
                    min-h-[90px] p-1.5 border-r border-gray-200 dark:border-gray-700 last:border-r-0
                    transition-colors cursor-pointer
                    ${!inMonth ? 'bg-gray-50/50 dark:bg-gray-800/30 opacity-40' : ''}
                    ${isWeekend && inMonth ? 'bg-gray-50 dark:bg-gray-800/50' : ''}
                    ${today ? 'bg-blue-50 dark:bg-blue-900/10 ring-1 ring-inset ring-blue-200 dark:ring-blue-800' : ''}
                    ${inMonth ? 'hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''}
                  `}
                >
                  {/* Day Number */}
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`
                        text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
                        ${today
                          ? 'bg-blue-600 text-white'
                          : inMonth
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-400 dark:text-gray-600'
                        }
                      `}
                    >
                      {day.date.getDate()}
                    </span>
                  </div>

                  {/* Summary: show all tasks + absences + hours */}
                  {hasData && inMonth && (
                    <div className="space-y-0.5 overflow-hidden">
                      {day.tasks.length > 0 && (
                        <div className="space-y-0.5 max-h-[120px] overflow-y-auto">
                          {day.tasks.map((t, idx) => (
                            <div key={idx} className="text-[10px] text-gray-600 dark:text-gray-400 truncate" title={t.taskName}>
                              <span className="font-medium text-gray-700 dark:text-gray-300">{t.taskName}</span>
                              <span className="text-gray-400 dark:text-gray-500 ml-0.5">{formatHours(t.durationHours)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {day.absenceCount > 0 && (
                        <div className="text-xs truncate">
                          <Badge variant="warning" className="!text-[10px] !px-1.5 !py-0">
                            {day.absenceCount} Abw.
                          </Badge>
                        </div>
                      )}
                      {day.totalPlannedHours > 0 && day.tasks.length === 0 && (
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                          {formatHours(day.totalPlannedHours)} geplant
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
